import { useQuery } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { Calendar, Building2, Users, CheckCircle, Trophy, TrendingUp, Target, Sparkles, Video, Clock } from 'lucide-react';
import { schedulesAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import { parseISO, isPast, isToday, isFuture, startOfMonth, endOfMonth, startOfYear, endOfYear, format } from 'date-fns';

// Helper function to convert UUID to short ID (same as Sidebar)
const getShortId = (id) => {
  if (!id) return null;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString().slice(-6).padStart(4, '0');
};

export default function CustomerDashboardPage() {
  const { user, studio } = useAuth();
  const studioId = studio?.id || user?.studioId;

  // Fetch upcoming schedules
  const { data: schedules } = useQuery({
    queryKey: ['schedules', studioId],
    queryFn: () => schedulesAPI.getSchedules(studioId),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });

  // Fetch student's bookings
  const { data: myBookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });

  const myBookings = myBookingsData?.data || myBookingsData || [];

  // Get thumbnails for booked classes (cached globally)
  const videoIds = useMemo(() =>
    [...new Set(myBookings.map(b => b.schedule?.videoId).filter(Boolean))],
    [myBookings]
  );
  const thumbnailsMap = useThumbnails(videoIds);

  const studioShortId = studioId ? getShortId(studioId) : null;

  // Calculate upcoming classes from customer's bookings
  const upcomingClasses = useMemo(() => {
    return myBookings
      .filter(booking => {
        if (!booking.schedule?.specificDate) return false; // Skip recurring for now
        const classDate = parseISO(booking.schedule.specificDate);
        return isFuture(classDate) || isToday(classDate);
      })
      .map(booking => ({
        ...booking,
        classDate: parseISO(booking.schedule.specificDate),
        className: booking.schedule?.video?.title || 'Class',
        startTime: booking.schedule?.startTime || '09:00:00',
        duration: booking.schedule?.video?.duration || 0
      }))
      .sort((a, b) => a.classDate - b.classDate)
      .slice(0, 5); // Show top 5 upcoming
  }, [myBookings]);

  // Calculate past classes metrics
  const pastClassesMetrics = useMemo(() => {
    const pastBookings = myBookings.filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const classDate = parseISO(booking.schedule.specificDate);
      return isPast(classDate) && !isToday(classDate);
    });

    const totalPastClasses = pastBookings.length;
    
    // Classes this month
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    const thisMonthClasses = pastBookings.filter(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      return classDate >= monthStart && classDate <= monthEnd;
    }).length;

    // Classes this year
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    const thisYearClasses = pastBookings.filter(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      return classDate >= yearStart && classDate <= yearEnd;
    }).length;

    // Streak calculation (consecutive months with at least one class)
    const classesByMonth = {};
    pastBookings.forEach(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      const monthKey = format(classDate, 'yyyy-MM');
      classesByMonth[monthKey] = (classesByMonth[monthKey] || 0) + 1;
    });
    
    const sortedMonths = Object.keys(classesByMonth).sort().reverse();
    let currentStreak = 0;
    let checkMonth = format(now, 'yyyy-MM');
    for (let i = 0; i < sortedMonths.length; i++) {
      if (sortedMonths[i] === checkMonth || 
          (i > 0 && sortedMonths[i] === format(new Date(parseISO(sortedMonths[i-1] + '-01')).setMonth(new Date(parseISO(sortedMonths[i-1] + '-01')).getMonth() - 1), 'yyyy-MM'))) {
        currentStreak++;
        const prevMonth = new Date(parseISO(checkMonth + '-01'));
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        checkMonth = format(prevMonth, 'yyyy-MM');
      } else {
        break;
      }
    }

    // Motivational message
    const getMotivationalMessage = () => {
      if (totalPastClasses === 0) {
        return { message: "Start your journey!", emoji: "🌟" };
      } else if (totalPastClasses < 5) {
        return { message: "Great start!", emoji: "💪" };
      } else if (totalPastClasses < 10) {
        return { message: "You're on fire!", emoji: "🔥" };
      } else if (totalPastClasses < 25) {
        return { message: "Amazing progress!", emoji: "⭐" };
      } else if (totalPastClasses < 50) {
        return { message: "Incredible dedication!", emoji: "🏆" };
      } else {
        return { message: "You're a legend!", emoji: "👑" };
      }
    };

    return {
      totalPastClasses,
      thisMonthClasses,
      thisYearClasses,
      currentStreak,
      motivational: getMotivationalMessage()
    };
  }, [myBookings]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Dashboard</h1>
        <p className="text-[var(--text-secondary)] mt-2">Welcome to your customer portal</p>
      </div>

      {/* Studio Info Card */}
      <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-[var(--text-secondary)]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {studio?.name || 'Your Studio'}
              </h2>
              {studioShortId && (
                <p className="text-sm text-[var(--text-secondary)] mt-1 font-mono">
                  Studio ID: {studioShortId}
                </p>
              )}
              {studio?.address && (
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  {studio.address}, {studio.city}, {studio.state} {studio.zipCode}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">My Bookings</p>
              <p className="text-3xl font-bold text-[var(--text-primary)] group-hover:text-primary-800 transition-colors">{myBookings.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
              <Calendar className="w-6 h-6 text-[var(--text-secondary)] group-hover:text-[var(--text-secondary)] transition-colors" />
            </div>
          </div>
        </div>
        <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Upcoming Classes</p>
              <p className="text-3xl font-bold text-[var(--text-primary)] group-hover:text-primary-800 transition-colors">{upcomingClasses.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <CheckCircle className="w-6 h-6 text-green-600 group-hover:text-green-700 transition-colors" />
            </div>
          </div>
        </div>
        <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Past Classes</p>
              <p className="text-3xl font-bold text-[var(--text-primary)] group-hover:text-primary-800 transition-colors">{pastClassesMetrics.totalPastClasses}</p>
              {pastClassesMetrics.thisMonthClasses > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1">{pastClassesMetrics.thisMonthClasses} this month</p>
              )}
            </div>
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center group-hover:bg-accent-200 transition-colors">
              <Trophy className="w-6 h-6 text-accent-600 group-hover:text-accent-700 transition-colors" />
            </div>
          </div>
        </div>
        <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1 group-hover:text-[var(--text-secondary)] transition-colors">Month Streak</p>
              <p className="text-3xl font-bold text-[var(--text-primary)] group-hover:text-primary-800 transition-colors">{pastClassesMetrics.currentStreak}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">consecutive months</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <Target className="w-6 h-6 text-purple-600 group-hover:text-purple-700 transition-colors" />
            </div>
          </div>
        </div>
      </div>

      {/* Progress Card - Show if user has past classes */}
      {pastClassesMetrics.totalPastClasses > 0 && (
        <div className="card bg-gradient-to-br from-primary-50 to-accent-50 border-2 border-[var(--border-subtle)] hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-[var(--text-secondary)]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-1">
                  Your Progress {pastClassesMetrics.motivational.emoji}
                </h3>
                <p className="text-[var(--text-secondary)] font-medium">
                  {pastClassesMetrics.motivational.message} You've completed {pastClassesMetrics.totalPastClasses} classes total!
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-[var(--text-secondary)] mb-1">This Year</p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{pastClassesMetrics.thisYearClasses}</p>
              <p className="text-xs text-[var(--text-muted)]">classes</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[var(--text-secondary)]">Fitness Journey</p>
              <p className="text-sm font-bold text-[var(--text-primary)]">
                {Math.min(100, Math.round((pastClassesMetrics.totalPastClasses / 50) * 100))}%
              </p>
            </div>
            <div className="w-full bg-primary-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-primary-500 to-accent-500 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, Math.round((pastClassesMetrics.totalPastClasses / 50) * 100))}%` }}
              />
            </div>
            {pastClassesMetrics.totalPastClasses < 50 && (
              <p className="text-xs text-[var(--text-secondary)] mt-2">
                {50 - pastClassesMetrics.totalPastClasses} more classes to reach the next milestone!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Classes */}
      <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Upcoming Classes</h3>
        {upcomingClasses.length > 0 ? (
          <div className="space-y-3">
            {upcomingClasses.map((booking) => {
              const video = booking.schedule?.video || {};
              const [hours, minutes] = booking.startTime.split(':');
              const hour12 = parseInt(hours) % 12 || 12;
              const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
              const formattedTime = `${hour12}:${minutes} ${ampm}`;
              const durationMinutes = Math.floor((booking.duration || 0) / 60);
              const classIsToday = isToday(booking.classDate);
              
              return (
                <div
                  key={booking.id}
                  className={`relative flex items-stretch rounded-lg border group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 overflow-visible ${
                    classIsToday
                      ? 'border-green-200'
                      : 'border-[var(--border-subtle)]'
                  }`}
                  style={{ background: classIsToday ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-elevated)' }}
                >
                  {classIsToday && (
                    <div className="absolute -top-2 right-14 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md z-10">
                      Today
                    </div>
                  )}

                  {/* Thumbnail Section */}
                  <div className="relative w-24 flex-shrink-0 bg-gradient-to-br from-primary-100 to-secondary-100">
                    {thumbnailsMap[booking.schedule?.videoId] ? (
                      <img
                        src={thumbnailsMap[booking.schedule?.videoId]}
                        alt={booking.className}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-8 h-8 text-[var(--text-muted)]" />
                      </div>
                    )}

                    {/* Duration badge on thumbnail */}
                    {durationMinutes > 0 && (
                      <div className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {durationMinutes}m
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="flex-1 flex items-center justify-between p-3">
                    <div className="flex-1">
                      <p className="font-semibold text-[var(--text-primary)]">{booking.className}</p>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {format(booking.classDate, 'EEEE, MMM d')} at {formattedTime}
                      </p>
                      {video.instructor && (
                        <p className="text-xs text-[var(--text-muted)] mt-0.5">
                          with {video.instructor.firstName} {video.instructor.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Calendar icon on the right */}
                  <div className={`w-12 flex items-center justify-center flex-shrink-0 ${
                    classIsToday ? 'bg-green-100' : 'bg-primary-50'
                  }`}>
                    <Calendar className={`w-5 h-5 ${classIsToday ? 'text-green-600' : 'text-[var(--text-secondary)]'}`} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <p className="text-[var(--text-secondary)] mb-2">No upcoming classes scheduled</p>
            <p className="text-sm text-[var(--text-muted)]">Book a class from the Studio Schedule to see it here!</p>
          </div>
        )}
      </div>
    </div>
  );
}

